import BlogPost from 'components/blog/blog-post';
import Container from 'components/container';
import Link from 'next/link';
import { LIBUM_GLACI_PITCH_DECK_BLOG_URL, LIBUM_URL, LINKEDIN_URL, N8N_URL } from 'shared/constants/globals';
import { BLOG_ITEMS } from 'shared/data/blogs';

const BLOG = BLOG_ITEMS.find((b) => b.id === 'the-only-symitar-quest-overlay')!;

export default function TheOnlySymitarQuestOverlay() {
  const title = `${BLOG.title} | Libum`;
  const description = BLOG.description;
  const keywords = BLOG.keywords.join(',');
  const url = `${LIBUM_URL}/blog/${BLOG.id}`;

  return (
    <Container title={title} description={description} keywords={keywords} url={url} socialCard="blog">
      <div style={{ width: '100%' }}>
        <BlogPost blog={BLOG}>
          <p>Yes, you read that right - we&apos;ve built an overlay to augment Symitar Quest.</p>
          <p>
            Now, you might be wondering what the actually means for your credit union. Well, to put it simply, through{' '}
            <Link href="/glaci" target="_blank">
              Glaci
            </Link>{' '}
            we&apos;re introducing a <b>new spatial dimension</b> to Quest that your team can design custom experiences
            on with beautiful UIs and powerful controls.
          </p>
          <p>
            With Glaci, your teams will be able to use{' '}
            <Link href={N8N_URL} target="_blank">
              n8n
            </Link>
            -like controls to build <b>entire experiences</b> that interact with the Quest UI and{' '}
            <b>trigger based on conditions you determine</b> given the{' '}
            <b style={{ color: '#00D68F' }}>state of the member</b>, <b style={{ color: '#00D68F' }}>account history</b>
            , or even <b style={{ color: '#00D68F' }}>transaction data</b>.
          </p>
          <div className="divider" />
          <h2 className="anchor" id="the-future-of-custom">
            <Link href="#the-future-of-custom">The future of custom</Link>
          </h2>
          <p>
            There&apos;s simply never a shortage of ideas when it comes to improving processes and creating efficiencies
            within a credit union&apos;s operations. However, the reality is that most of the ideas are met with the
            friction of having to both <b>build, maintain, and update the solution</b> and{' '}
            <b>keeping it beautiful and cohesive</b>. Not only is it usually quite <b>expensive</b>, but if you do
            manage to build something - the <b>technical debt</b> you inherit is often overwhelming.
          </p>
          <p>
            This is where we come in. <b>Our team takes on the technical burden</b> of building and maintaining the
            platform & design, while your team retains the creative freedom to orchestrate{' '}
            <b>your experiences, your way</b> <img src="/icons/twitter/fire.png" alt="Fire emoji" className="icon" />
          </p>
          <h3 className="anchor" id="building-a-creative-platform">
            <Link href="#building-a-creative-platform">Building a creative platform</Link>
          </h3>
          <p>
            We understand that credit unions are creative. Their internal processes can be incredibly custom and will
            likely have unique requirements that they can&apos;t quite capture in bespoke unilateral solutions that are
            pre-packaged by any vendor. This is why we&apos;re going the creative platform route - we figured,{' '}
            <b>why not just give the credit unions a packaged platform where we consistently grow the capabilities</b>{' '}
            and they can just <b style={{ color: '#00D68F' }}>build what they want</b> using our tools?
          </p>
          <p>
            That&apos;s what{' '}
            <Link href="/glaci" target="_blank">
              Glaci
            </Link>{' '}
            is - a creative platform that empowers credit unions to build <b>beautiful</b>, <b>custom experiences</b>{' '}
            that <b>augment Symitar Quest</b>.
          </p>
          <figure>
            <img
              src="/images/4fa3b48c-85e6-466a-a189-7bf48bd1a72e.jpg"
              alt="A screenshot of the Glaci platform giving details on a few notable components"
            />
            <figcaption>Slide 9 from EAP pitch deck showcasing main Glaci UI</figcaption>
          </figure>
          <h3 className="anchor" id="why-an-overlay">
            <Link href="#why-an-overlay">Why an overlay?</Link>
          </h3>
          <p>
            An overlay offers a layer of augmentation to an existing application that is not limited to its original
            functionality. This means, we at{' '}
            <Link href={LINKEDIN_URL} target="_blank">
              Libum
            </Link>{' '}
            get to express our own creative freedom to address the needs of the Jack Henry credit union community.
          </p>
          <p>
            By building an overlay, we can create <b>custom experiences</b> that just sort of{' '}
            <i>&quot;sits on top of&quot;</i> the Quest UI yet have the ability to interact with it. For example,
            we&apos;ve created <b>quick navigation</b> to help users get to where they need to go faster. This is also
            just the beginning - we have a lot of ideas like <b>revamping common work areas</b> within Quest to make
            them more efficient and user-friendly{' '}
            <img src="/icons/twitter/party-popper.png" alt="Party popper emoji" className="icon" />
          </p>
          <figure>
            <img
              src="/images/0f9d07a0-f7a2-41f4-b307-53d3f9f366df.jpg"
              alt="A screenshot of the Glaci 'quick navigation' feature as an overlay within Quest"
            />
            <figcaption>
              Slide 10 from EAP pitch deck showcasing Glaci &apos;quick navigation&apos; feature in Quest
            </figcaption>
          </figure>
          <h3 className="anchor" id="extensible-adaptable-and-flexible">
            <Link href="#extensible-adaptable-and-flexible">Extensible, adaptable, and flexible</Link>
          </h3>
          <p>
            Now, if we&apos;re going to put all of this work into something - we have to build it to make it last. That
            said, basically everything client-side is built to receive{' '}
            <b style={{ color: '#00D68F' }}>over-the-air updates</b> without requiring any downtime, deployment, or
            manual intervention.
          </p>
          <p>
            You as the credit union will have the ability to control when updates are applied, and we will be{' '}
            <b>continuously shipping new features and improvements</b> to the platform. This means that your team can
            focus on building experiences that matter while we focus on making sure the platform is always improving{' '}
            <img src="/icons/twitter/sparkles.png" alt="Sparkles emoji" className="icon" />
          </p>
          <div className="divider" />
          <h2 className="anchor" id="work-with-the-founders">
            <Link href="#work-with-the-founders">Work with the Founders</Link>
          </h2>
          <p>
            We learned with creating <Link href="/poweron-pro">PowerOn Pro</Link> that if we want to build meaningful
            software that effectively bridges actual gaps in the space, then we must get it into the hands of its
            everyday users <b>as early as possible</b>{' '}
            <img src="/icons/twitter/rocket.png" alt="Rocket emoji" className="icon" />
          </p>
          <h3 className="anchor" id="early-adopter-program">
            <Link href="#early-adopter-program">
              Early Adopter Program <span className="subtitle">for Credit Unions</span>
            </Link>
          </h3>
          <p>
            That said, we will take on{' '}
            <b>
              <span style={{ color: '#00D68F' }}>3 to 5</span> credit unions
            </b>{' '}
            that are looking for innovative change in how they deliver solutions and are willing to communicate closely
            with our team to faciliate design decisions that best benefit the community. Members participating in this
            program will pay a flat rate annually and inherit the full suite of features available through the Glaci
            platform at no additional cost.
          </p>
          <p>
            Finally, if you think your credit union would be a good fit for the program, please take a look our{' '}
            <Link href={LIBUM_GLACI_PITCH_DECK_BLOG_URL} target="_blank">
              pitch deck
            </Link>{' '}
            and follow the next steps slide.
          </p>
        </BlogPost>
      </div>
    </Container>
  );
}
